<template>
  <div>
    <h1 class="mb-8 text-3xl font-bold">
      Dashboard
    </h1>
    <p class="mb-4 leading-normal">
      Selamat datang di aplikasi SAPBKPP
    </p>

    <p v-if="refreshedAtLabel" class="mb-4 text-sm text-gray-600 italic">
      Data Dashboard terakhir diperbaharui: <strong>{{ refreshedAtLabel }}</strong>
    </p>

    <div class="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2 xl:grid-cols-4">
      <component
        :is="stat.href ? 'inertia-link' : 'div'"
        v-for="stat in cards"
        :key="stat.label"
        v-bind="stat.href ? { href: stat.href } : {}"
        class="stats min-w-0 bg-base-100 shadow"
        :class="[stat.borderClass, stat.href ? 'hover:shadow-lg transition-shadow' : '']">
        <div class="stat">
          <div class="stat-figure">
            <span
              class="inline-flex items-center justify-center w-12 h-12 rounded-full text-white"
              :class="stat.iconBg">
              <i class="text-lg" :class="stat.icon" />
            </span>
          </div>
          <div class="stat-title whitespace-normal leading-snug">
            {{ stat.label }}
          </div>
          <div class="stat-value" :class="stat.valueClass">
            {{ stat.value }}
          </div>
          <div class="stat-desc" :class="stat.descClass">
            {{ stat.note }}
          </div>
        </div>
      </component>
    </div>

    <div class="card bg-base-100 shadow">
      <div class="card-body">
        <h2 class="card-title">
          Pertumbuhan Mahasiswa
          <span class="badge badge-outline">{{ yearRange }}</span>
        </h2>
        <Chart
          type="bar"
          :data="growthChartData"
          :options="growthChartOptions"
          :height="350"
          role="img"
          :aria-label="'Grafik batang pertumbuhan mahasiswa ' + yearRange" />
      </div>
    </div>

    <div class="card bg-base-100 shadow mt-4">
      <div class="card-body">
        <h2 class="card-title">
          Penyaluran Dana Beasiswa
          <span class="badge badge-outline">{{ yearRange }}</span>
        </h2>
        <Chart
          type="bar"
          :data="disbursementChartData"
          :options="disbursementChartOptions"
          :height="350"
          role="img"
          :aria-label="'Grafik batang penyaluran dana beasiswa per program tahun ' + yearRange" />
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Chart, { getThemeColor } from '@/Shared/Chart'
import { Link } from '@inertiajs/vue2'

export default {
    name: 'DashboardAdmin',
    layout: Layout,
    components: { Chart, 'inertia-link': Link },
    metaInfo: { title: 'Dashboard' },
    props: {
        stats: { type: Object, required: true },
        chartData: { type: Object, required: true },
    },
    computed: {
        scholarshipYear () {
            return this.$page.props.scholarship_year
        },
        yearRange () {
            const years = this.chartData.mahasiswaGrowth.years
            return `${years[0]}–${years[years.length - 1]}`
        },
        refreshedAt () {
            const times = [this.stats.generated_at, this.chartData.generated_at].filter(Boolean)
            if (!times.length) return null
            return times.reduce((a, b) => (new Date(a) <= new Date(b) ? a : b))
        },
        refreshedAtLabel () {
            if (!this.refreshedAt) return ''
            const parts = new Intl.DateTimeFormat('id-ID', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short',
            }).formatToParts(new Date(this.refreshedAt))
            const get = type => {
                const part = parts.find(p => p.type === type)
                return part ? part.value : ''
            }
            const time = [get('hour'), get('minute'), get('second')].join(':')
            return `${get('day')} ${get('month')} ${get('year')}, ${time} ${get('timeZoneName')}`.trim()
        },
        growthChartData () {
            const g = this.chartData.mahasiswaGrowth
            return {
                labels: g.years,
                datasets: [
                    {
                        label: 'Lama',
                        data: g.carried,
                        backgroundColor: getThemeColor('--p'),
                    },
                    {
                        label: 'Baru',
                        data: g['new'],
                        backgroundColor: getThemeColor('--a'),
                    },
                ],
            }
        },
        growthChartOptions () {
            return {
                plugins: {
                    legend: {
                        position: 'bottom',
                    },
                    tooltip: {
                        mode: 'index',
                        callbacks: {
                            footer (tooltipItems) {
                                let total = 0
                                tooltipItems.forEach((item) => {
                                    total += item.parsed.y
                                })
                                return 'Total: ' + total
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                    },
                    y: {
                        stacked: true,
                        title: {
                            display: true,
                            text: 'Jumlah Mahasiswa',
                        },
                    },
                },
            }
        },
        disbursementChartData () {
            const d = this.chartData.disbursement
            return {
                labels: d.years,
                datasets: [
                    {
                        label: 'Beasiswa KRS/KHS',
                        data: d.krs.total,
                        backgroundColor: getThemeColor('--p'),
                    },
                    {
                        label: 'Beasiswa EOS',
                        data: d.eos.total,
                        backgroundColor: getThemeColor('--su'),
                    },
                ],
            }
        },
        disbursementChartOptions () {
            const d = this.chartData.disbursement
            return {
                plugins: {
                    legend: {
                        position: 'bottom',
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const key = context.datasetIndex === 0 ? 'krs' : 'eos'
                                const beneficiaries = d[key].beneficiaries[context.dataIndex]
                                return ` ${context.dataset.label}: ${this.formatRupiahShort(context.parsed.y)} (${beneficiaries} mahasiswa penerima)`
                            },
                        },
                    },
                },
                scales: {
                    y: {
                        title: {
                            display: true,
                            text: 'Penyaluran (Rupiah)',
                        },
                        ticks: {
                            callback: (value) => this.formatRupiahShort(value),
                        },
                    },
                },
            }
        },
        cards () {
            return [
                { label: 'Total Mahasiswa', value: String(this.stats.total_mahasiswa), note: 'data aktif', icon: 'fas fa-users', iconBg: 'bg-primary', borderClass: 'border-2 border-primary', valueClass: 'text-3xl text-primary', href: '/mahasiswa' },
                { label: 'Menunggu Kelengkapan Data', value: String(this.stats.menunggu_data), note: 'status 1', icon: 'fas fa-folder-open', iconBg: 'bg-info', borderClass: 'border-2 border-info', valueClass: 'text-3xl text-info', href: '/mahasiswa?vstatus=1' },
                { label: 'Menunggu Proses Verifikasi', value: String(this.stats.menunggu_verifikasi), note: 'status 2', icon: 'fas fa-hourglass-half', iconBg: 'bg-warning', borderClass: 'border-2 border-warning', valueClass: 'text-3xl text-warning', href: '/mahasiswa?vstatus=2' },
                { label: 'Memenuhi Syarat', value: String(this.stats.memenuhi_syarat), note: 'status 3', icon: 'fas fa-user-check', iconBg: 'bg-success', borderClass: 'border-2 border-success', valueClass: 'text-3xl text-success', href: '/mahasiswa?vstatus=3' },
                { label: 'Tidak Memenuhi Syarat', value: String(this.stats.tidak_memenuhi_syarat), note: 'status 4', icon: 'fas fa-user-times', iconBg: 'bg-error', borderClass: 'border-2 border-error', valueClass: 'text-3xl text-error', href: '/mahasiswa?vstatus=4' },
                { label: 'Dana Beasiswa (KRS/KHS) Tersalurkan', value: this.formatRupiah(this.stats.dana_krs_tersalurkan), note: 'status bank 6', icon: 'fas fa-wallet', iconBg: 'bg-accent', borderClass: 'border-2 border-accent', valueClass: 'text-xl text-primary' },
                { label: 'Data Beasiswa (Akhir Studi) Tersalurkan', value: this.formatRupiah(this.stats.dana_eos_tersalurkan), note: 'status bank 6', icon: 'fas fa-graduation-cap', iconBg: 'bg-accent', borderClass: 'border-2 border-accent', valueClass: 'text-xl text-primary' },
            ]
        },
    },
    methods: {
        formatRupiah (value) {
            return 'Rp ' + Number(value || 0).toLocaleString('id-ID')
        },
        formatRupiahShort (value) {
            value = Number(value || 0)
            if (value >= 1e9) return 'Rp ' + (value / 1e9).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' M'
            if (value >= 1e6) return 'Rp ' + (value / 1e6).toLocaleString('id-ID', { maximumFractionDigits: 1 }) + ' jt'
            return 'Rp ' + value.toLocaleString('id-ID')
        },
    },
}
</script>
